import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import FormControl from '@material-ui/core/FormControl'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import WarningIcon from '@material-ui/icons/Warning'
import CustomDialog from '../custom-material/dialog/Dialog'
import CustomDialogTitle from '../custom-material/dialog/DialogTitle'
import { CenteredCustomTypography, BoldCenteredCustomTypography } from '../custom-material/typography/Typography'
import CustomButton, { CancelCustomButton } from '../custom-material/button/Button'
import { formatDateForInput } from '../../libs/utils'

const styles = theme => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px'
  },
  confirmContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    letterSpacing: '0.75px'
  },
  warningIcon: {
    color: 'white',
    marginRight: 6
  },
  selectInputBase: {
    outline: '1px solid rgba(0, 0, 0, 0.42)'
  },
  selectInputRoot: {
    fontSize: 15,
    height: 30,
    width: 285,
    outline: '1px solid rgba(0, 0, 0, 0.42)'
  },
  selectInput: {
    paddingLeft: 3
  },
  selectMenu: {
    paddingLeft: 6,
    height: 16,
    display: 'flex',
    alignItems: 'center'
  },
  selectMenuItem: {
    fontSize: 15
  },
  selectMenuItemEmpty: {
    color: 'rgba(0, 0, 0, 0.42)',
    fontSize: 14
  },
  dateContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  dateLabelFrom: {
    marginRight: 10
  },
  dateLabelTo: {
    marginRight: 10,
    marginLeft: 10
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    width: '50%'
  },
  confirmActionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly'
  }
})

class SuspendAllDialog extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      length: '',
      startDate: formatDateForInput(new Date().toISOString()),
      endDate: '',
      reason: '',
      displayConfirmation: false
    }

    this.onLengthChange = event => this.setState({length: event.target.value})
    this.onStartDateChange = event => this.setState({startDate: event.target.value})
    this.onEndDateChange = event => this.setState({endDate: event.target.value})
    this.onReasonChange = event => this.setState({reason: event.target.value})
    this.displayConfirmation = () => this.setState({displayConfirmation: true})
  }
  render () {
    const { classes, dialogOpen, handleCloseDialog, suspendAll, sectionName, type } = this.props
    const { length, startDate, endDate, reason, displayConfirmation } = this.state

    return (
      <CustomDialog open={dialogOpen} onClose={handleCloseDialog} aria-labelledby='suspend-all-dialog-title'>
        <CustomDialogTitle className={classes.dialogTitle} id='suspend-all-dialog-title' disableTypography>
          <WarningIcon className={classes.warningIcon} /> Warning
        </CustomDialogTitle>
        {displayConfirmation
          ? <div className={classes.confirmContainer}>
            <CenteredCustomTypography paragraph>Your new settings have been saved.</CenteredCustomTypography>
            <CenteredCustomTypography paragraph>Please allow 24 business hours to see email<br />preferences take effect.</CenteredCustomTypography>
            <div className={classes.confirmActionContainer}>
              <CustomButton variant='contained' size='small' color='secondary'  onClick={handleCloseDialog}>OK</CustomButton>
            </div>
          </div>
          : <div className={classes.contentContainer}>
            <CenteredCustomTypography paragraph>You are about to suspend</CenteredCustomTypography>
            <BoldCenteredCustomTypography paragraph>{sectionName}</BoldCenteredCustomTypography>
            <CenteredCustomTypography paragraph>How long would you like to suspend your emails?</CenteredCustomTypography>
            <br />
            <FormControl>
              <Select
                className={classes.selectInputBase}
                value={length}
                onChange={this.onLengthChange}
                MenuProps={{classes: {paper: classes.selectMenuPaper}}}
                classes={{selectMenu: classes.selectMenu}}
                required
                error={!length}
                displayEmpty
                inputProps={{
                  name: 'suspend-length',
                  id: 'suspend-length',
                  classes: {
                    root: classes.selectInputRoot
                  }
                }}
              >
                <MenuItem value=''>
                  <span className={classes.selectMenuItemEmpty}>Suspension Length</span>
                </MenuItem>
                <MenuItem className={classes.selectMenuItem} value='Indefinitely'>Indefinitely</MenuItem>
                <MenuItem className={classes.selectMenuItem} value='Specified Date Range'>Specified Date Range</MenuItem>
              </Select>
            </FormControl>
            <br /><br />
            {length === 'Specified Date Range'
              ? <div className={classes.dateContainer}>
                <span className={classes.dateLabelFrom}>from</span>
                <TextField
                  id='startDate'
                  type='date'
                  value={formatDateForInput(startDate)}
                  InputProps={{classes: {root: classes.input, input: classes.inputText, disabled: classes.inputDisabled}}}
                  required
                  error={!startDate}
                  onChange={this.onStartDateChange}
                />
                <span className={classes.dateLabelTo}>to</span>
                <TextField
                  id='endDate'
                  type='date'
                  value={formatDateForInput(endDate)}
                  InputProps={{classes: {root: classes.input, input: classes.inputText, disabled: classes.inputDisabled}}}
                  required
                  error={!endDate || (endDate < startDate)}
                  onChange={this.onEndDateChange}
                />
              </div>
              : null
            }
            {length ? <span><br /><br /></span> : null}
            {!length
              ? null
              : <FormControl>
                <Select
                  className={classes.selectInputBase}
                  value={reason}
                  onChange={this.onReasonChange}
                  MenuProps={{classes: {paper: classes.selectMenuPaper}}}
                  classes={{selectMenu: classes.selectMenu}}
                  required
                  error={!reason}
                  displayEmpty
                  inputProps={{
                    name: 'suspend-reason',
                    id: 'suspend-reason',
                    classes: {
                      root: classes.selectInputRoot
                    }
                  }}
                >
                  <MenuItem value=''>
                    <span className={classes.selectMenuItemEmpty}>Suspension Reason</span>
                  </MenuItem>
                  <MenuItem className={classes.selectMenuItem} value='I get too many emails'>I get too many emails</MenuItem>
                  <MenuItem className={classes.selectMenuItem} value='I am going on leave'>I am going on leave</MenuItem>
                  <MenuItem className={classes.selectMenuItem} value='These emails do not pertain to me'>These emails do not pertain to me</MenuItem>
                  <MenuItem className={classes.selectMenuItem} value='Other'>Other</MenuItem>
                </Select>
              </FormControl>
            }
            {length ? <span><br /><br /><br /></span> : null}
            <div className={classes.actionContainer}>
              <CancelCustomButton variant='contained' size='small' color='default'  onClick={handleCloseDialog}>Cancel</CancelCustomButton>
              <CustomButton
                disabled={!length || (length === 'Specified Date Range' && (!startDate || !endDate)) || !reason}
                variant='contained'
                size='small'
                color='secondary'
                
                onClick={() => { suspendAll(startDate, endDate, reason, type)(); this.displayConfirmation() }}
              >
                Save
              </CustomButton>
            </div>
          </div>
        }
      </CustomDialog>
    )
  }
}

SuspendAllDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  dialogOpen: PropTypes.bool.isRequired,
  handleCloseDialog: PropTypes.func.isRequired,
  suspendAll: PropTypes.func.isRequired,
  sectionName: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired
}

export default withStyles(styles)(SuspendAllDialog)
